import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { IconButton, IconDropdown } from '../../../common/controls/Controls';
import { useSelector } from 'react-redux';
import { QuestionModal } from '../../../common';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteFilled,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import checkPermission from '../../../../utils/helpers/checkPermission';
import useIsUserAdmin from '../../../../hooks/useIsUserAdmin';
import { isUndefined } from 'lodash';
import useHelpWithModal from '../../../../hooks/useHelpWithModal.jsx';
import BaseHtmlModal from '../../../common/modals/RawHtmlModal/BaseHtmlModal.jsx';

const AppBarCMS = ({
  backUrl,
  backUrlTooltip,
  title,
  mainActions,
  secondaryActions,
  createUrl,
  createText,
  onDelete,
  permissionsBasic,
  backAction,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const isSidebarHidden = useSelector(
    (state) => state.localStorage.isSidebarHidden,
  );
  const helpEnabled = import.meta.env.VITE_HELP_ENABLED === 'true';

  const isAdmin = useIsUserAdmin();

  const [confirmationAction, setConfirmationAction] = useState(undefined); // if there is action: {onClick, confirmation} modal is displayed and onClick is made after confirm

  const {
    helpDetail,
    helpActions,
    modal,
  } = useHelpWithModal();

  const canDelete = useMemo(
    () => checkPermission(user, `${permissionsBasic}.destroy`, isAdmin),
    [permissionsBasic, user, isAdmin],
  );


  const canCreate = useMemo(
    () => checkPermission(user, `${permissionsBasic}.store`, isAdmin),
    [permissionsBasic, user, isAdmin],
  );

  const decoratedMainActions = useMemo(() => {
    const actions = [];

    if (!isUndefined(createUrl) && !isUndefined(createText) && canCreate) {
      actions.push({
        onClick: () => navigate(createUrl),
        title: createText,
        icon: <PlusOutlined />,
      });
    }

    const filtered = Array.isArray(mainActions)
      ? mainActions.filter((mainAction) =>
        isUndefined(mainAction.permission)
          || checkPermission(user, mainAction.permission, isAdmin)
      )
      : [];

    const helpFiltered =
      helpDetail?.data && !helpDetail?.error && helpEnabled
        ? helpActions
        : [];

    return [
      ...actions,
      ...filtered,
      ...helpFiltered,
    ];
  }, [
    canCreate,
    createText,
    createUrl,
    isAdmin,
    mainActions,
    navigate,
    user,
    helpActions,
    helpDetail,
  ]);
  const renderSecondaryActions = () => {
    if (decoratedSecondaryActions.length > 0) {
      return <IconDropdown items={decoratedSecondaryActions} />;
    }
    return null;
  };

  const decoratedSecondaryActions = useMemo(() => {
    const allSecondaryActions = [];

    if (!isUndefined(onDelete) && canDelete) {
      allSecondaryActions.push({
        onClick: () => onDelete(),
        title: t('Vymazať'),
        icon: <DeleteFilled />,
        confirmation: t('Naozaj chcete vymazať túto položku?'),
      });
    }

    if (isUndefined(secondaryActions)) {
      return allSecondaryActions;
    }

    const filtered = secondaryActions.filter(
      // remove actions which are not permitted
      (secondaryAction) =>
        checkPermission(user, secondaryAction.permission, isAdmin),
    );

    return [...filtered, ...allSecondaryActions];
  }, [canDelete, isAdmin, onDelete, secondaryActions, t, user]);

  /*if (helpDetail?.isLoadingHelp) {
    return <Spinner />;
  }*/

  return (
    <>
      <div
        className={`app-bar-header-container ${
          isSidebarHidden ? 'menu-closed' : ''
        }`}
      >
        <div className="app-bar-wrapper">
          <div className="left-content-container">
            {backUrl && (
              <IconButton
                type="button"
                tooltip={backUrlTooltip}
                onClick={() => navigate(backUrl)}
                icon={<ArrowLeftOutlined />}
              />
            )}
            {backAction && (
              <IconButton
                type="button"
                tooltip={backAction.tooltip}
                onClick={() =>
                  (backAction.onClick && backAction.onClick()) ||
                  navigate(backAction.url)
                }
                icon={<ArrowLeftOutlined />}
              />
            )}
            {title && <div className="main-bar-title">{title}</div>}
          </div>
          <div className="right-controls-container">
            {decoratedMainActions.length > 0 &&
              decoratedMainActions.map((action) => (
                <IconButton
                  type="button"
                  key={`icon-btn-${action.title}`}
                  onClick={
                    action.confirmation
                      ? setConfirmationAction(action)
                      : action.onClick
                  }
                  tooltip={action.title}
                  icon={action.icon}
                  disabled={action.disabled}
                  disabledTooltip={action.disabledTooltip}
                />
              ))}
            {renderSecondaryActions()}
          </div>
        </div>
      </div>

      <QuestionModal
        chosenPositive={() => confirmationAction.onClick()}
        question={confirmationAction && confirmationAction.confirmation}
        visible={(confirmationAction && true) || false}
        yes={t('Áno')}
        toggle={() => setConfirmationAction(undefined)}
        headerTitle={t('Potvrdzovací dialóg')}
      />

      {helpDetail?.data && !helpDetail?.error && helpEnabled && (
        <BaseHtmlModal
          visible={modal.props.visible}
          headerTitle={modal.props.headerTitle}
          content={modal.props.content}
          toggle={modal.props.toggle}
        />
      )}


    </>
  );
};

AppBarCMS.propTypes = {
  backUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // DEPRECATED -> use backAction TODO: refactor
  backUrlTooltip: PropTypes.string, // DEPRECATED -> use backAction
  backAction: PropTypes.object, // {onClick: url, tooltip}
  title: PropTypes.string,
  mainActions: PropTypes.array, // [{onCLick, url, title, icon, confirmation}] confirmation is just text which will sidplay confirmation modal and when user choose positive onClick will be made
  secondaryActions: PropTypes.array, // [{onCLick, url, title, icon, confirmation}]
  createUrl: PropTypes.string,
  createText: PropTypes.string,
  onDelete: PropTypes.func,
  permissionsBasic: PropTypes.string,
};

export default AppBarCMS;
